/**
 * O que o front DECLARA ao back em toda request (`X-Omni-*`) e no `auth` do socket (faixa 39 §1–2): build, fuso e
 * tela. Só pra mostrar no log — o back nada decide por isso. Valor fora do formato do back
 * (`retrato_acesso/declarado.py`) não vai: lá viraria `null` de qualquer jeito. Ver `declaradoDoCliente.md`.
 */

export type Declarado = { build?: string; fuso?: string; tela?: string }

const BUILD = /^[0-9a-f]{7,40}$/
// Começa com `/`, até 256 ASCII imprimíveis (`declarado.py::tela_valida`); o `pathname` já vem sem query nem hash.
const TELA = /^\/[\x20-\x7e]{0,255}$/

/** `process.env.OMNI_BUILD` literal: o Next só o inlina assim (`next.config.ts` → `env`). */
const build = (): string | undefined => {
  const sha = process.env.OMNI_BUILD
  return sha && BUILD.test(sha) ? sha : undefined
}

const fuso = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

const tela = (): string | undefined => {
  if (typeof window === 'undefined') return undefined
  const caminho = window.location.pathname
  return TELA.test(caminho) ? caminho : undefined
}

export const declaradoDoCliente = (): Declarado => {
  const retrato: Declarado = {}
  const b = build()
  const f = fuso()
  const t = tela()
  if (b) retrato.build = b
  if (f) retrato.fuso = f
  if (t) retrato.tela = t
  return retrato
}

const HEADER: Record<keyof Declarado, string> = {
  build: 'X-Omni-Build',
  fuso: 'X-Omni-Fuso',
  tela: 'X-Omni-Tela',
}

/** Os `X-Omni-*` de uma request: só dos campos válidos, nunca header vazio. */
export const cabecalhosDeclarados = (): Record<string, string> =>
  Object.fromEntries(Object.entries(declaradoDoCliente()).map(([campo, valor]) => [HEADER[campo as keyof Declarado], valor]))

/** `auth` do socket: o mesmo retrato + `reconexao` (quem sabe se é reconexão é o `socket.ts`). */
export const declaradoDoSocket = (reconexao: boolean): Declarado & { reconexao: boolean } => ({
  ...declaradoDoCliente(),
  reconexao,
})
